/**
 * Session Manager - Central session orchestration
 * Manages session state across all three role types: user, admin, nominee
 * Enforces single-session policy with role-switch confirmation
 */

import logger from './logger';

// Session type constants
export const SESSION_TYPES = {
  USER: 'user',
  ADMIN: 'admin',
  NOMINEE: 'nominee',
  NONE: null
};

// Storage key definitions for each role
export const STORAGE_KEYS = {
  user: {
    token: 'token',
    data: 'user',
    masterKey: '__mk'
  },
  admin: {
    token: 'adminToken',
    data: 'adminData'
  },
  nominee: {
    token: 'nomineeToken',
    email: 'nomineeEmail',
    nomineeMasterKey: '__nmk',
    vaultMasterKey: '__vmk',
    selectedVault: 'selectedVaultUserId',
    selectedVaultName: 'selectedVaultUserName',
    encryptedMasterKeyShare: 'encryptedMasterKeyShare'
  }
};

/**
 * Detect currently active session type
 * Priority order: user > admin > nominee
 * @returns {string|null} - SESSION_TYPES.USER, .ADMIN, .NOMINEE, or .NONE
 */
export const detectActiveSession = () => {
  // Check in priority order
  if (localStorage.getItem(STORAGE_KEYS.user.token)) {
    return SESSION_TYPES.USER;
  }
  if (localStorage.getItem(STORAGE_KEYS.admin.token)) {
    return SESSION_TYPES.ADMIN;
  }
  if (localStorage.getItem(STORAGE_KEYS.nominee.token)) {
    return SESSION_TYPES.NOMINEE;
  }
  return SESSION_TYPES.NONE;
};

/**
 * Check if user is attempting to switch to a different role
 * @param {string} targetRole - Target role type
 * @returns {boolean} - True if switching roles
 */
export const isRoleSwitch = (targetRole) => {
  const currentRole = detectActiveSession();
  return currentRole !== SESSION_TYPES.NONE && currentRole !== targetRole;
};

/**
 * Get active session information
 * @returns {Object} - { type, token, data }
 */
export const getActiveSessionInfo = () => {
  const sessionType = detectActiveSession();

  if (sessionType === SESSION_TYPES.NONE) {
    return { type: SESSION_TYPES.NONE, token: null, data: null };
  }

  const keys = STORAGE_KEYS[sessionType];
  const token = localStorage.getItem(keys.token);
  const data = keys.data ? JSON.parse(localStorage.getItem(keys.data) || '{}') : null;

  return { type: sessionType, token, data };
};

/**
 * Clear all session data for a specific role
 * @param {string} role - Role type to clear
 */
export const clearRoleSession = (role) => {
  if (!role || role === SESSION_TYPES.NONE) return;

  const keys = STORAGE_KEYS[role];
  if (!keys) {
    logger.error(`Invalid role type: ${role}`);
    return;
  }

  logger.log(`[SessionManager] Clearing ${role} session`);

  // Clear localStorage
  Object.values(keys).forEach(key => {
    localStorage.removeItem(key);
  });

  // Clear sessionStorage
  if (role === SESSION_TYPES.USER) {
    sessionStorage.removeItem(STORAGE_KEYS.user.masterKey);
    // Temp DEK parked during MFA; removed on MFA success but lingers if the
    // user abandons MFA. Clear it so no plaintext key survives logout.
    sessionStorage.removeItem('temp_mk');
    // Clear cached nominee list so the next account does not inherit a stale
    // count (which would wrongly skip the "Nominee Required" gate on login).
    sessionStorage.removeItem('__nominee_cache');
    sessionStorage.removeItem('__nominee_cache_timestamp');
  } else if (role === SESSION_TYPES.NOMINEE) {
    sessionStorage.removeItem(STORAGE_KEYS.nominee.nomineeMasterKey);
    sessionStorage.removeItem(STORAGE_KEYS.nominee.vaultMasterKey);
    sessionStorage.removeItem(STORAGE_KEYS.nominee.selectedVault);
    sessionStorage.removeItem(STORAGE_KEYS.nominee.selectedVaultName);
    sessionStorage.removeItem(STORAGE_KEYS.nominee.encryptedMasterKeyShare);
    // Pending vault-link list from login; not a STORAGE_KEY, leaks across
    // nominee accounts on the same device if not cleared.
    sessionStorage.removeItem('pendingVaults');
  }

  logger.log(`[SessionManager] ${role} session cleared successfully`);
};

/**
 * Clear all sessions (full logout from all roles)
 */
export const clearAllSessions = () => {
  logger.log('[SessionManager] Clearing all sessions');

  Object.keys(SESSION_TYPES).forEach(roleKey => {
    const role = SESSION_TYPES[roleKey];
    if (role) {
      clearRoleSession(role);
    }
  });

  logger.log('[SessionManager] All sessions cleared');
};

/**
 * Get role-specific dashboard route
 * @param {string} role - Role type
 * @returns {string} - Dashboard route path
 */
export const getRoleDashboardRoute = (role) => {
  const routes = {
    [SESSION_TYPES.USER]: '/dashboard',
    [SESSION_TYPES.ADMIN]: '/admin/dashboard',
    [SESSION_TYPES.NOMINEE]: '/nominee/dashboard'
  };
  return routes[role] || '/';
};

/**
 * Get role-specific login route
 * @param {string} role - Role type
 * @returns {string} - Login route path
 */
export const getRoleLoginRoute = (role) => {
  const routes = {
    [SESSION_TYPES.USER]: '/signin',
    [SESSION_TYPES.ADMIN]: '/admin',
    [SESSION_TYPES.NOMINEE]: '/nominee/login'
  };
  return routes[role] || '/signin';
};

/**
 * Get friendly role name for display
 * @param {string} role - Role type
 * @returns {string} - Display-friendly role name
 */
export const getRoleDisplayName = (role) => {
  const names = {
    [SESSION_TYPES.USER]: 'User',
    [SESSION_TYPES.ADMIN]: 'Admin',
    [SESSION_TYPES.NOMINEE]: 'Nominee'
  };
  return names[role] || 'Unknown';
};

/**
 * Get role-specific BroadcastChannel name
 * @param {string} role - Role type
 * @returns {string} - Channel name
 */
export const getRoleChannelName = (role) => {
  const channels = {
    [SESSION_TYPES.USER]: 'digitalvault-session-user',
    [SESSION_TYPES.ADMIN]: 'digitalvault-session-admin',
    [SESSION_TYPES.NOMINEE]: 'digitalvault-session-nominee'
  };
  return channels[role] || 'digitalvault-session';
};

/**
 * Get role-specific inactivity timeout (in minutes)
 * @param {string} role - Role type
 * @returns {number} - Timeout in minutes
 */
export const getRoleInactivityTimeout = (role) => {
  const timeouts = {
    [SESSION_TYPES.USER]: 30,     // 30 minutes
    [SESSION_TYPES.ADMIN]: 15,    // 15 minutes (stricter for security)
    [SESSION_TYPES.NOMINEE]: 20   // 20 minutes
  };
  return timeouts[role] || 30;
};

/**
 * Check if a session is valid (has token)
 * @param {string} role - Role type
 * @returns {boolean} - True if session exists
 */
export const hasValidSession = (role) => {
  if (!role || role === SESSION_TYPES.NONE) return false;

  const keys = STORAGE_KEYS[role];
  if (!keys) return false;

  return !!localStorage.getItem(keys.token);
};

/**
 * Validate session and perform cleanup if needed
 * @returns {Object} - { valid: boolean, type: string }
 */
export const validateCurrentSession = () => {
  const activeSession = detectActiveSession();

  if (activeSession === SESSION_TYPES.NONE) {
    return { valid: false, type: SESSION_TYPES.NONE };
  }

  // Check if token exists
  const hasToken = hasValidSession(activeSession);

  if (!hasToken) {
    // Token missing but other data exists - cleanup
    clearRoleSession(activeSession);
    return { valid: false, type: SESSION_TYPES.NONE };
  }

  return { valid: true, type: activeSession };
};

export default {
  SESSION_TYPES,
  STORAGE_KEYS,
  detectActiveSession,
  isRoleSwitch,
  getActiveSessionInfo,
  clearRoleSession,
  clearAllSessions,
  getRoleDashboardRoute,
  getRoleLoginRoute,
  getRoleDisplayName,
  getRoleChannelName,
  getRoleInactivityTimeout,
  hasValidSession,
  validateCurrentSession
};
